import { useEffect, useState } from 'react'
import { useSpring, animated } from 'react-spring'

import gsap from 'gsap/dist/gsap'

import { MotionPathPlugin } from 'gsap/dist/MotionPathPlugin'

import { IMAGES_COUNT, SPRING_CONFIG, SPRITE_COLS, SPRITE_ROWS } from '~/components/Chest/constants'
import { getSpriteSize } from '~/components/Chest/helpers'

import ChestLoader from '~/components/Chest/components/ChestLoader'
import ChestSpriteImages from '~/components/Chest/components/ChestSpriteImages'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import { GemType } from '~/components/Rock/types'
import BootcampRewards from '~/shared/components/RewardsChest/BootcampRewards'
import Rewards from '~/shared/components/RewardsChest/Rewards'
import { ChestSize } from '~/components/Chest/types'

import AnimationBubble from './AnimationBubble'

// Register MotionPathPlugin here once,
// note: remove it will break the gems, xp, and orbs animation
gsap.registerPlugin(MotionPathPlugin)

interface Props {
  startAnimation: boolean
  hasBackgroundBubble?: boolean
  isOpen?: boolean
  rewards?: {
    xp: number
    gemCount: number
    gemType: GemType
    kudos: number
  }
  size?: ChestSize
  rewardSize?: ChestSize
  isLargeReward?: boolean
  isRewardDelay?: boolean
  transformOrigin?: string
  onAnimationComplete?: () => void
}

const RewardsChest = ({
  startAnimation,
  rewards,
  rewardSize,
  size,
  hasBackgroundBubble = true,
  isOpen = false,
  isLargeReward = false,
  isRewardDelay = false,
  transformOrigin,
  onAnimationComplete,
}: Props): React.ReactElement => {
  const [didOpenChest, setDidOpenChest] = useState(false)
  const [executeAnimation, setExecuteAnimation] = useState(false)
  const [frame, setFrame] = useState(isOpen ? IMAGES_COUNT : 1)
  const [spriteImageDidLoad, setSpriteImageDidLoad] = useState(false)
  const isInitialFrame = frame === 1

  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.LG })
  const defaultSize = isDesktop ? 'small' : 'x-small'

  const spring = useSpring(spriteImageDidLoad ? SPRING_CONFIG : {})

  const [spriteWidth, spriteHeight] = getSpriteSize(size ? size : defaultSize)
  const imageHeight = spriteHeight / SPRITE_ROWS
  const imageWidth = spriteWidth / SPRITE_COLS

  useEffect(() => {
    const handleOnAnimationDidEnd = (): void => {
      setDidOpenChest(true)
      onAnimationComplete?.()
    }

    if (isInitialFrame && executeAnimation && startAnimation) {
      const interval = setInterval(() => {
        setFrame((prevState) => {
          if (prevState === IMAGES_COUNT) {
            handleOnAnimationDidEnd()
            clearInterval(interval)
            return prevState
          } else {
            return prevState + 1
          }
        })
      }, 80)
    }
  }, [executeAnimation, isInitialFrame, startAnimation, onAnimationComplete])

  useEffect(() => {
    setDidOpenChest(startAnimation)
    setFrame(1)
    setExecuteAnimation(startAnimation)
  }, [startAnimation])

  return (
    <div className="relative pl-1.5 md:pl-2.5 md:-mt-2 lg:-mt-1">
      <ChestLoader
        containerHeight={imageHeight}
        containerWidth={imageWidth}
        displayLoader={!spriteImageDidLoad}
        size={isDesktop ? 'small' : 'x-small'}
      />
      <animated.div
        className={`relative z-10 ${spriteImageDidLoad ? 'block' : 'hidden'}`}
        data-test-id="reward-chest"
        style={{ minWidth: imageWidth, minHeight: imageHeight, transformOrigin, ...spring }}
      >
        <ChestSpriteImages
          className="absolute max-w-none !m-0"
          enhanceSpriteSheet
          enhanceSpriteSheetMultiplier={3}
          frame={frame}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
          setSpriteImageDidLoad={setSpriteImageDidLoad}
          spriteCols={SPRITE_COLS}
          spriteHeight={spriteHeight}
          spriteImageDidLoad={spriteImageDidLoad}
          spriteImgSrcKey="gamification/chests/reward-chest_upscale-spritesheet_v4.png"
          spriteRows={SPRITE_ROWS}
          spriteWidth={spriteWidth}
        />
      </animated.div>
      {didOpenChest &&
        executeAnimation &&
        (rewards ? (
          <Rewards rewards={rewards} rewardSize={rewardSize} size={size} />
        ) : (
          <BootcampRewards isDelay={isRewardDelay} isLarge={isLargeReward} />
        ))}
      {startAnimation && hasBackgroundBubble && (
        <AnimationBubble onAnimationComplete={() => setExecuteAnimation(true)} />
      )}
    </div>
  )
}

export default RewardsChest
